import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  TrendingUp, 
  TrendingDown, 
  Plus, 
  Search, 
  X,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  LineChart,
  BarChart3
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import AppLayout from '../components/AppLayout';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [equityCurve, setEquityCurve] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingSymbol, setAddingSymbol] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [portfolioRes, watchlistRes, equityRes] = await Promise.all([
        api.get('/portfolio/summary'),
        api.get('/watchlist'),
        api.get('/portfolio/equity-curve')
      ]);
      setPortfolio(portfolioRes.data);
      setWatchlist(watchlistRes.data);
      setEquityCurve(equityRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const searchStocks = async (query) => {
    if (query.length < 1) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await api.get(`/stocks/search?q=${query}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      searchStocks(searchQuery);
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const addToWatchlist = async (symbol) => {
    setAddingSymbol(symbol);
    try {
      const response = await api.post(`/watchlist?symbol=${symbol}`);
      
      // Check for upgrade prompt
      if (response.data.upgrade_prompt) {
        toast.warning(response.data.upgrade_prompt, {
          action: {
            label: 'Upgrade',
            onClick: () => navigate('/settings')
          },
          duration: 6000
        });
      } else {
        toast.success(`Added ${symbol} to watchlist`);
      }
      
      setSearchQuery('');
      setSearchResults([]);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to add to watchlist';
      if (detail.includes('Upgrade to Pro')) {
        toast.error(detail, {
          action: {
            label: 'Upgrade',
            onClick: () => navigate('/settings')
          },
          duration: 6000
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setAddingSymbol(null);
    }
  };

  const removeFromWatchlist = async (symbol) => {
    try {
      await api.delete(`/watchlist/${symbol}`);
      toast.success(`Removed ${symbol} from watchlist`);
      fetchData();
    } catch (error) {
      toast.error('Failed to remove from watchlist');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    const prefix = value >= 0 ? '+' : '';
    return `${prefix}${value.toFixed(2)}%`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-sm font-semibold text-[#1A4D2E]">
            {formatCurrency(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-pulse-subtle text-primary font-serif text-xl">Loading dashboard...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white border-stone-100 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Total Equity</p>
                  <p className="text-2xl font-bold text-[#1F2937] tabular-nums mt-1" data-testid="total-equity">
                    {formatCurrency(portfolio?.total_equity || 10000)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-[#1A4D2E]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-stone-100 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Cash Balance</p>
                  <p className="text-2xl font-bold text-[#1F2937] tabular-nums mt-1" data-testid="cash-balance">
                    {formatCurrency(portfolio?.cash_balance || 10000)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-stone-100 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Total P&L</p>
                  <p className={`text-2xl font-bold tabular-nums mt-1 ${portfolio?.total_pl >= 0 ? 'text-green-600' : 'text-red-500'}`} data-testid="total-pl">
                    {formatCurrency(portfolio?.total_pl || 0)}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${portfolio?.total_pl >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  {portfolio?.total_pl >= 0 ? (
                    <ArrowUpRight className="w-6 h-6 text-green-600" />
                  ) : (
                    <ArrowDownRight className="w-6 h-6 text-red-500" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-stone-100 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Day Change</p>
                  <p className={`text-2xl font-bold tabular-nums mt-1 ${portfolio?.day_change >= 0 ? 'text-green-600' : 'text-red-500'}`} data-testid="day-change">
                    {formatPercent(portfolio?.day_change_percent || 0)}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${portfolio?.day_change >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <LineChart className={`w-6 h-6 ${portfolio?.day_change >= 0 ? 'text-green-600' : 'text-red-500'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Equity Curve */}
          <Card className="lg:col-span-2 bg-white border-stone-100 shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-xl text-[#1F2937]">Portfolio Performance</CardTitle>
            </CardHeader>
            <CardContent>
              {equityCurve.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={equityCurve}>
                      <defs>
                        <linearGradient id="equityGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1A4D2E" stopOpacity={0.1}/>
                          <stop offset="95%" stopColor="#1A4D2E" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="date" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#6B7280' }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#6B7280' }}
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="equity"
                        stroke="#1A4D2E"
                        strokeWidth={2}
                        fill="url(#equityGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex flex-col items-center justify-center text-gray-500">
                  <LineChart className="w-12 h-12 mb-4 opacity-30" />
                  <p>Make some trades to see your performance chart</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-white border-stone-100 shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-xl text-[#1F2937]">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                onClick={() => navigate('/portfolio')}
                className="w-full bg-[#1A4D2E] hover:bg-[#143d24] text-white justify-start"
                data-testid="view-portfolio-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                View Portfolio
              </Button>
              <Button 
                onClick={() => navigate('/stock/AAPL')}
                variant="outline"
                className="w-full border-[#1A4D2E]/20 text-[#1A4D2E] hover:bg-green-50 justify-start"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Explore AAPL
              </Button>
              <Button 
                onClick={() => navigate('/settings')}
                variant="outline"
                className="w-full border-stone-200 text-gray-600 hover:bg-gray-50 justify-start"
              >
                <Search className="w-4 h-4 mr-2" />
                Account Settings
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Watchlist Section */}
        <Card className="bg-white border-stone-100 shadow-card">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="font-serif text-xl text-[#1F2937]">Watchlist</CardTitle>
              
              {/* Search Input */}
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search stocks to add..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white border-stone-200 focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/10"
                  data-testid="stock-search-input"
                />
                {searchQuery && (
                  <button 
                    onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
                
                {/* Search Results Dropdown */}
                {searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-stone-200 rounded-lg shadow-float z-50 max-h-60 overflow-auto">
                    {searchResults.map((stock) => (
                      <div
                        key={stock.symbol}
                        className="flex items-center justify-between p-3 hover:bg-green-50 cursor-pointer border-b border-stone-100 last:border-0"
                        onClick={() => addToWatchlist(stock.symbol)}
                        data-testid={`search-result-${stock.symbol}`}
                      >
                        <div>
                          <p className="font-semibold text-[#1F2937]">{stock.symbol}</p>
                          <p className="text-xs text-gray-500 truncate max-w-[200px]">{stock.description}</p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          disabled={addingSymbol === stock.symbol}
                          className="text-[#1A4D2E]"
                        >
                          {addingSymbol === stock.symbol ? (
                            <span className="w-4 h-4 border-2 border-green-200 border-t-green-600 rounded-full animate-spin"></span>
                          ) : (
                            <Plus className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {watchlist.length === 0 ? (
              <div className="text-center py-12">
                <TrendingUp className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-2">Your watchlist is empty</p>
                <p className="text-sm text-gray-400">Search for stocks above to add them</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left border-b border-stone-100">
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Symbol</th>
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Price</th>
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Change</th>
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">High/Low</th>
                      <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {watchlist.map((stock) => (
                      <tr 
                        key={stock.symbol} 
                        className="border-b border-stone-50 hover:bg-green-50/30 cursor-pointer transition-colors"
                        onClick={() => navigate(`/stock/${stock.symbol}`)}
                        data-testid={`watchlist-item-${stock.symbol}`}
                      >
                        <td className="py-4">
                          <div>
                            <p className="font-semibold text-[#1F2937]">{stock.symbol}</p>
                            <p className="text-xs text-gray-500 truncate max-w-[150px]">{stock.name}</p>
                          </div>
                        </td>
                        <td className="py-4">
                          <p className="font-medium tabular-nums">{formatCurrency(stock.current_price)}</p>
                        </td>
                        <td className="py-4">
                          <div className={`flex items-center gap-1 ${stock.percent_change >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {stock.percent_change >= 0 ? (
                              <TrendingUp className="w-4 h-4" />
                            ) : (
                              <TrendingDown className="w-4 h-4" />
                            )}
                            <span className="font-medium tabular-nums">{formatPercent(stock.percent_change)}</span>
                          </div>
                        </td>
                        <td className="py-4">
                          <p className="text-sm text-gray-500 tabular-nums">
                            {formatCurrency(stock.high)} / {formatCurrency(stock.low)}
                          </p>
                        </td>
                        <td className="py-4 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromWatchlist(stock.symbol);
                            }}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default DashboardPage;
